import { TextField } from '@material-ui/core';
import DataType from '@opsbase/lib-datatype/DataType';
import i18n from '@opsbase/lib-i18n';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf/useSelf';
import useOpDefinitionCopy from '@opsbase/react-gql-hooks/opdefinition/useOpDefinitionCopy';
import useOpDefinitionCreate from '@opsbase/react-gql-hooks/opdefinition/useOpDefinitionCreate';
import useOpDefinitionCreateFromTemplate from '@opsbase/react-gql-hooks/opdefinition/useOpDefinitionCreateFromTemplate';
import useOpDefinitions from '@opsbase/react-gql-hooks/opdefinition/useOpDefinitions';
import useOrgTemplates from '@opsbase/react-gql-hooks/orgtemplate/useOrgTemplates';
import useTemplates from '@opsbase/react-gql-hooks/template/useTemplates';
import useNewDataActionsContext from '@opsbase/web-ctrl-newdata/useNewDataActions';
import OpDefinitionChooser from '@opsbase/web-data-choosers/OpDefinitionChooser';
import OrganisationChooser from '@opsbase/web-data-choosers/OrganisationChooser';
import OrgTemplateChooser from '@opsbase/web-data-choosers/OrgTemplateChooser';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import WizardDialog from '@opsbase/web-dialogs/WizardDialog';
import Card from '@opsbase/web-layout/Card';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav/useNav';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import Checkbox from '@opsbase/web-ui/Checkbox/Checkbox';
import Radio from '@opsbase/web-ui/Radio/Radio';
import RadioGroup from '@opsbase/web-ui/RadioGroup';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TemplateChooser from '../../../shared/UX/TemplateChooser';
import OpDefinitionCreateConfigurations from './OpDefinitionCreateConfigurations';
// eslint-disable-next-line no-shadow
export var CreateType;
(function (CreateType) {
    CreateType["EMPTY"] = "empty";
    CreateType["CLONE"] = "clone";
    CreateType["CLONEOTHER"] = "cloneother";
    CreateType["TEMPLATE"] = "template";
})(CreateType || (CreateType = {}));
// eslint-disable-next-line no-shadow
export var TemplateType;
(function (TemplateType) {
    TemplateType["ORG"] = "org";
    TemplateType["SYSTEM"] = "system";
})(TemplateType || (TemplateType = {}));
export function OpDefinitionCreateDialog(props) {
    const { initialStep = 0, initialType = CreateType.EMPTY } = props;
    const [title, setTitle] = useState('');
    const [type, setType] = useState(initialType);
    const [templateType, setTemplateType] = useState(TemplateType.ORG);
    const [selectedOpDefinitions, setSelectedOpDefinitions] = useState([]);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [selectedOrgs, setSelectedOrgs] = useState([]);
    const [newDefinitions, setNewDefinitions] = useState([]);
    const [shouldCopyBusinessRules, setShouldCopyBusinessRules] = useState(false);
    const { raise } = useNotificationActions();
    const { goBack, replaceTo } = useNav();
    const self = useSelf(true);
    const createOpDefinition = useOpDefinitionCreate();
    const createOpDefinitionsFromTemplate = useOpDefinitionCreateFromTemplate();
    const copyOpDefinitions = useOpDefinitionCopy();
    const { addItemsToType } = useNewDataActionsContext();
    const { data: opDefinitions } = useOpDefinitions({
        variables: {
            id: selectedOpDefinitions.map(s => s.id),
            organisation: (selectedOrgs === null || selectedOrgs === void 0 ? void 0 : selectedOrgs.length) ? selectedOrgs.map(o => o.id) : []
        },
        skip: !(selectedOpDefinitions === null || selectedOpDefinitions === void 0 ? void 0 : selectedOpDefinitions.length),
    });
    const { data: orgTemplates } = useOrgTemplates({
        variables: {
            id: selectedTemplates.map(s => s.id),
        },
        skip: !(selectedTemplates === null || selectedTemplates === void 0 ? void 0 : selectedTemplates.length),
    });
    const { data: templates } = useTemplates({
        variables: {
            id: selectedTemplates.map(s => s.id),
        },
        skip: !(selectedTemplates === null || selectedTemplates === void 0 ? void 0 : selectedTemplates.length),
    });
    useEffect(() => {
        setNewDefinitions((opDefinitions || [])
            .concat(templates || [])
            .concat(orgTemplates || [])
            .map((d) => {
            var _a, _b, _c;
            return {
                id: d.id,
                title: d.title,
                description: d.description ? d.description : '',
                places: d.places ? d.places : [],
                teams: d.teams ? d.teams : undefined,
                positions: d.positions ? d.positions : undefined,
                enabled: true,
                allPTsPermitted: (_a = d.allPTsPermitted) !== null && _a !== void 0 ? _a : undefined,
                isScoreEnabled: (_b = d.isScoreEnabled) !== null && _b !== void 0 ? _b : undefined,
                type: (_c = d.type) !== null && _c !== void 0 ? _c : undefined,
            };
        }));
    }, [opDefinitions, orgTemplates, templates]);
    const doSetNewOpDefinitions = useMemo(() => v => {
        if (!v) {
            v = [
                {
                    title: title,
                },
            ];
        }
        setNewDefinitions(v.map(d => {
            var _a, _b;
            return ({
                id: d.id,
                title: d.title,
                description: d.description ? d.description : '',
                places: d.places ? d.places : [],
                teams: d.teams ? d.teams : undefined,
                positions: d.positions ? d.positions : undefined,
                enabled: true,
                allPTsPermitted: (_a = d.allPTsPermitted) !== null && _a !== void 0 ? _a : undefined,
                isScoreEnabled: (_b = d.isScoreEnabled) !== null && _b !== void 0 ? _b : undefined,
            });
        }));
    }, [title]);
    const handleSelectOrg = useCallback(newSelectedOrgs => {
        setSelectedOrgs(newSelectedOrgs);
        setSelectedOpDefinitions([]);
    }, []);
    const handleSelectOpDefinition = useCallback(newSelectedOpDefinitions => {
        setSelectedOpDefinitions(newSelectedOpDefinitions);
        setSelectedTemplates([]);
    }, []);
    const handleSelectTemplate = useCallback(newSelectedTemplates => {
        setSelectedTemplates(newSelectedTemplates);
        setSelectedOpDefinitions([]);
    }, []);
    const handleRemoveOpDefinition = useCallback(removeOpDefinition => {
        let opDefinitionsAfterRemove = newDefinitions.filter(d => d !== removeOpDefinition);
        setNewDefinitions(opDefinitionsAfterRemove);
        if (type === CreateType.TEMPLATE) {
            opDefinitionsAfterRemove = selectedTemplates.filter(d => d.title !== removeOpDefinition.title);
            setSelectedTemplates(opDefinitionsAfterRemove);
        }
        else if (type === CreateType.CLONE || type === CreateType.CLONEOTHER) {
            opDefinitionsAfterRemove = selectedOpDefinitions.filter(d => d.title !== removeOpDefinition.title);
            setSelectedOpDefinitions(opDefinitionsAfterRemove);
        }
        else {
            // new manual opplan creation
            // cannot delete in case of new manual operation creation
        }
    }, [newDefinitions, type, selectedTemplates, selectedOpDefinitions]);
    const handleChangeType = useCallback(newType => setType(newType), []);
    const handleChangeTemplateType = useCallback(newTemplateType => {
        handleSelectTemplate([]);
        setTemplateType(newTemplateType);
    }, [handleSelectTemplate]);
    const handleAllowNext = useCallback(stepNumber => {
        if (stepNumber === 2) {
            return type === 'template' && templateType === TemplateType.ORG
                ? newDefinitions.every(p => p.places.length)
                : newDefinitions.every(o => !!o.type);
        }
        return true;
    }, [newDefinitions, templateType, type]);
    const handleValidate = useCallback(async (step = 100000) => {
        switch (type) {
            case CreateType.EMPTY:
                return step === 0 || !!title;
            case CreateType.CLONE:
                return step === 0 || selectedOpDefinitions.length > 0;
            case CreateType.CLONEOTHER:
                return step === 0 || selectedOpDefinitions.length > 0;
            case CreateType.TEMPLATE:
                return step === 0 || selectedTemplates.length > 0;
        }
        return true;
    }, [selectedOpDefinitions, selectedTemplates, title, type]);
    const handleComplete = useCallback(async () => {
        await handleValidate();
        const patches = newDefinitions.map(d => {
            var _a, _b, _c;
            return {
                title: d.title,
                description: d.description,
                enabled: d.enabled,
                isScoreEnabled: (_a = d.isScoreEnabled) !== null && _a !== void 0 ? _a : true,
                places: d.places.map(({ id }) => ({ id })),
                allPTsPermitted: d.allPTsPermitted,
                teams: (_b = d.teams) === null || _b === void 0 ? void 0 : _b.map(({ id }) => ({ id })),
                positions: (_c = d.positions) === null || _c === void 0 ? void 0 : _c.map(({ id }) => ({ id })),
                type: d.type || undefined,
            };
        });
        // CREATE AN EMPTY OPERATION
        if (type === CreateType.EMPTY) {
            return raise(createOpDefinition({
                patch: { ...patches[0] },
            }).then(res => {
                addItemsToType(DataType.OPDEFINITION, [res]);
                replaceTo(Pages.OP_DEFINITIONS_EDIT, { id: res.id });
            }), i18n.t('opdefinition-created-successful'));
        }
        else if (type === CreateType.TEMPLATE) {
            return raise(createOpDefinitionsFromTemplate({
                ids: selectedTemplates.map(d => d.id),
                patches: patches,
                templateType,
            }).then(res => {
                addItemsToType(DataType.OPDEFINITION, res);
                goBack();
            }), i18n.t('opdefinition-created-successful'));
        }
        // CLONE AN EXISTING OPERATION
        else {
            return raise(copyOpDefinitions({
                organisationId: selectedOrgs.length ? selectedOrgs[0].id : undefined,
                ids: selectedOpDefinitions.map(d => d.id),
                patches: patches,
                shouldCopyBusinessRules,
            }).then(res => {
                addItemsToType(DataType.OPDEFINITION, res);
                goBack();
            }), i18n.t('opdefinition-created-successful'));
        }
    }, [
        handleValidate,
        newDefinitions,
        type,
        raise,
        createOpDefinition,
        addItemsToType,
        replaceTo,
        createOpDefinitionsFromTemplate,
        selectedTemplates,
        templateType,
        goBack,
        copyOpDefinitions,
        selectedOrgs,
        selectedOpDefinitions,
        shouldCopyBusinessRules,
    ]);
    const steps = useMemo(() => {
        return [
            React.createElement(Card, { padding: true },
                React.createElement(RadioGroup, { value: type, onChange: handleChangeType, options: [
                        { value: CreateType.EMPTY, label: i18n.t('create-empty-op-plan') },
                        {
                            value: CreateType.TEMPLATE,
                            label: i18n.t('create-op-plan-from-template'),
                        },
                        { value: CreateType.CLONE, label: i18n.t('copy-op-plans') },
                        self.isSuperUser && {
                            value: CreateType.CLONEOTHER,
                            label: i18n.t('copy-op-plans-from-org'),
                        },
                    ].filter(x => !!x) })),
            type === CreateType.EMPTY && (React.createElement(TextField, { style: { backgroundColor: Palette.White.main }, label: i18n.t('title'), autoFocus: true, color: "primary", value: title, onChange: e => setTitle(e.target.value), onBlur: () => doSetNewOpDefinitions(undefined), margin: "dense", variant: "outlined", fullWidth: true })),
            type === CreateType.CLONE && (React.createElement(Vertical, { gutter: true },
                React.createElement(OpDefinitionChooser, { multi: true, value: selectedOpDefinitions, selectLabel: i18n.t('select-op-plan'), onChange: handleSelectOpDefinition, filters: { organisation: selectedOrgs.map(o => o.id) } }),
                React.createElement(Checkbox, { label: i18n.t('copy-business-rules'), checked: shouldCopyBusinessRules, onChange: setShouldCopyBusinessRules }))),
            type === CreateType.CLONEOTHER && (React.createElement(Vertical, { gutter: true },
                React.createElement(Text, null, i18n.t('select-org')),
                React.createElement(OrganisationChooser, { forceReal: true, value: selectedOrgs, onChange: handleSelectOrg }),
                !!selectedOrgs && (React.createElement(OpDefinitionChooser, { multi: true, forceReal: true, value: selectedOpDefinitions, selectLabel: i18n.t('select-op-plan'), onChange: handleSelectOpDefinition, filters: { organisation: selectedOrgs.map(o => o.id) } })))),
            type === CreateType.TEMPLATE && (React.createElement(Vertical, null,
                React.createElement(RadioGroup, { value: templateType, onChange: handleChangeTemplateType },
                    React.createElement(Radio, { key: TemplateType.ORG, value: TemplateType.ORG, checked: templateType === TemplateType.ORG, label: i18n.t('select-org-templates') }),
                    templateType === TemplateType.ORG && (React.createElement(OrgTemplateChooser, { value: selectedTemplates, multi: true, onChange: handleSelectTemplate })),
                    React.createElement(Radio, { key: TemplateType.SYSTEM, value: TemplateType.SYSTEM, checked: templateType === TemplateType.SYSTEM, label: i18n.t('select-system-templates') }),
                    templateType === TemplateType.SYSTEM && (React.createElement(TemplateChooser, { selectedItems: selectedTemplates.map(s => ({
                            ...s,
                            type: DataType.TEMPLATE,
                        })), onChange: handleSelectTemplate }))))),
            React.createElement(OpDefinitionCreateConfigurations, { type: type === CreateType.TEMPLATE && templateType === TemplateType.ORG
                    ? 'orgtemplate'
                    : '', value: newDefinitions, onChange: setNewDefinitions, onRemove: handleRemoveOpDefinition }),
        ].filter(x => !!x);
    }, [
        type,
        handleChangeType,
        self,
        title,
        selectedOpDefinitions,
        handleSelectOpDefinition,
        selectedOrgs,
        shouldCopyBusinessRules,
        handleSelectOrg,
        templateType,
        handleChangeTemplateType,
        selectedTemplates,
        handleSelectTemplate,
        newDefinitions,
        handleRemoveOpDefinition,
        doSetNewOpDefinitions,
    ]);
    return (React.createElement(WizardDialog, { closable: false, size: DialogSize.X1000_Y500, title: i18n.t('create-op-plan'), steps: steps, initialStep: initialStep, allowNext: handleAllowNext, validate: handleValidate, onComplete: handleComplete }));
}
export default OpDefinitionCreateDialog;
